// Domain related to GIC Offers
import { HrefParams } from '../../../directives/href/href.directive';
import { Currency } from '../../../domain/market.domain';

import { OrderExtendedTerms } from './order.domain';
import { EquityOrderAction } from './order.enum';

export enum GicType {
  SHORT = 'SHORT',
  LONG = 'LONG',
  CASHABLE = 'CASHABLE',
  MARKET_LINKED = 'MARKET_LINKED'
}

export enum PaymentFrequency {
  MONTHLY = 'MONTHLY',
  SEMIANNUAL = 'SEMIANNUAL',
  ANNUAL = 'ANNUAL',
  COMPOUNDED_ANNUAL = 'COMPOUNDED_ANNUAL', // Only used on UI to ensure we check the correct field
  END_OF_TERM = 'END_OF_TERM' // translates to At Maturity
}

export enum ShortTermGicTerm {
  SHORT_30 = 30,
  SHORT_60 = 60,
  SHORT_90 = 90,
  SHORT_120 = 120,
  SHORT_180 = 180,
  SHORT_270 = 270
}

export interface GicResponse {
  businessDate: Date;
  gicRatesResponse: GicList;
}

export interface GicList {
  [GicType.SHORT]: Array<ShortTermGic>;
  [GicType.LONG]: Array<LongTermGic>;
  [GicType.CASHABLE]: Array<CashableGic>;
  [GicType.MARKET_LINKED]: Array<MarketLinkedGic>;
}

// List of possible term lengths for GIC types
// string arrays for use for template tabs
export interface GicTermList {
  LONG: Array<string>;
  CASHABLE: Array<string>;
  MARKET_LINKED: Array<string>;
}

export interface GicTradeParams extends HrefParams {
  orderType: string;
  symbol: string;
  securityType: string;
  gicType: string;
  gicTerm: string;
  gicFrequency: string;
  gicRate: number;
}

export interface BaseGic {
  gicType: GicType;
  issuerName: string;
  currencyCd: Currency;
}

export interface GicWithTerm {
  termLength: number;
}

export interface GicWithRates<T> extends BaseGic {
  gicRates: T;
}

export type ShortTermGic = GicWithRates<ShortTermGicMap>;

export interface LongTermGic extends GicWithTerm, GicWithRates<LongTermGicMap>  {

}

export interface CashableGic extends GicWithTerm, GicWithRates<CashableGicMap> {
  preEncashmentScheduleList: Array<PreEncashmentRate>;
}

export interface MarketLinkedGic extends GicWithTerm, GicWithRates<MarketLinkedGicMap> {
  linkedIndexName: string;
  guaranteedMinimumReturnRate: number;
  guaranteedMaximumReturnRate: number;
}

export interface ShortTermGicMap {
  SHORT_30: ShortTermGicRates;
  SHORT_60: ShortTermGicRates;
  SHORT_90: ShortTermGicRates;
  SHORT_120: ShortTermGicRates;
  SHORT_180: ShortTermGicRates;
  SHORT_270: ShortTermGicRates;
}

export interface LongTermGicMap {
  MONTHLY: BaseGicRates;
  SEMIANNUAL: BaseGicRates;
  ANNUAL: BaseGicRates;
  COMPOUNDED_ANNUAL?: BaseGicRates;
}

export interface CashableGicMap {
  MONTHLY?: BaseGicRates;
  END_OF_TERM?: BaseGicRates;
  ANNUAL?: BaseGicRates;
}

export interface MarketLinkedGicMap {
  COMPOUNDED_ANNUAL: BaseGicRates;
}

export interface BaseGicRates {
  interestPaymentFrequencyTypeCd: PaymentFrequency;
  interestCompoundFrequencyTypeCd?: PaymentFrequency; // not sent when GIC isn't compounded
  minimumBuyAmt: number;
  maximumBuyAmt: number;
  nonRegisteredAccountMinimumBuyAmt?: number;
  tfsaAccountMinimumBuyAmt?: number;
  rspAccountMinimumBuyAmt?: number;
  respAccountMinimumBuyAmt?: number;
  rifAccountMinimumBuyAmt?: number;
  rate: number;
  symbol: string;
}

export interface PreEncashmentRate {
  interestRate: number;
  schedulePeriodVal: string;
}

export interface GicRatesAccountMinimum extends BaseGicRates {
  selectedAccountMinimumBuyAmt?: number; // used for displaying min on order ticket search
}

export interface ShortTermGicRates extends BaseGicRates {
  termLength: ShortTermGicTerm;
}

export interface ShortGicTerms {
  translation: string;
  value: ShortTermGicTerm;
  minDate: Date;
  maxDate: Date;
}

// used to create the results table/track selected gic, flattened values
export interface GicResultsItem {
  gicType: GicType;
  issuerName: string;
  currencyCd: Currency;
  termLength: number | ShortTermGicTerm;
  linkedIndexName?: string;
  guaranteedMinimumReturnRate?: number;
  guaranteedMaximumReturnRate?: number;
  gicRate: GicRatesAccountMinimum;
  preEncashmentScheduleList?: Array<PreEncashmentRate>;
}

export interface GicExtendedTerms extends OrderExtendedTerms {
  amount: number;
  orderAction: EquityOrderAction;
  termInYears?: number;
  termInDays?: number;
  startDate?: Date;
  maturityDate?: Date;
  ratePercent: number;
  compoundFrequency: PaymentFrequency;
  rolloverInstructions: string; // TODO: check typing
}

